import * as React from 'react';
import {
  Body1,
  Body2,
  Button,
  Caption2Strong,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Image,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { defineMessage, useIntl } from 'react-intl';
import { TextLink } from '@foundershub/components';
import step1Image from '../media/copilotFreStep1.webp';
import step2Image from '../media/copilotFreStep2.webp';
import step3Image from '../media/copilotFreStep3.webp';
import copilotTerms from '../media/MicrosoftForStartupsCopilotSupplementalTerms.pdf';

export function CopilotFirstTime() {
  const intl = useIntl();
  const [step, setStep] = React.useState<number>(0);
  const classes = useStyles();

  const steps = [
    {
      image: step1Image,
      title: defineMessage({
        defaultMessage: 'Introducing Copilot in Founders Hub',
        description: 'step title',
      }),
      subtitle: defineMessage({
        defaultMessage: 'Powered by Azure OpenAI Service',
        description: 'step subtitle',
      }),
      copy: defineMessage({
        defaultMessage:
          'Founders Hub is now powered by AI to help you on your startup journey. Use Copilot in Founders Hub to get answers to questions you may have on navigating Founders Hub, building with Azure, or leveraging essential Founders Hub benefits.',
        description: 'step description',
      }),
      allowBack: false,
    },
    {
      image: step2Image,
      title: defineMessage({
        defaultMessage: 'Use your own judgement',
        description: 'step title',
      }),
      copy: defineMessage({
        defaultMessage:
          'Copilot generates fluent, grammatical text but can’t evaluate the accuracy. Responses include references when possible, so you can verify the content. If Copilot is not able to help with your request, Copilot will ask you to submit a support ticket to get help from our team.',
        description: 'step description',
      }),
      allowBack: true,
    },
    {
      image: step3Image,
      title: defineMessage({
        defaultMessage: 'Provide feedback',
        description: 'step title',
      }),
      copy: defineMessage({
        defaultMessage:
          'Your feedback will help improve this experience. Use the feedback buttons to share your insights. Conversation and feedback data will be stored to monitor quality and improve accuracy. Copilot does not have access to your Founders Hub account or user data.{br}{br}By using Copilot in Founders Hub you consent to the <termslink>Copilot supplemental terms</termslink>.',
        description: 'step description',
      }),
      allowBack: true,
    },
  ];

  const { image, title, subtitle, copy, allowBack } = steps[step];

  const handleNext = () => {
    setStep((step) => step + 1);
  };

  return (
    <Dialog defaultOpen={true}>
      <DialogSurface>
        <DialogBody className={classes.body}>
          <span className={classes.header}>
            <span className={classes.previewLabel}>
              <Caption2Strong>{intl.formatMessage(previewLabel)}</Caption2Strong>
            </span>
            <DialogTrigger>
              <Button appearance="transparent" icon={<Dismiss20Regular />} />
            </DialogTrigger>
          </span>
          <Image src={image} className={classes.image} />
          <div className={classes.title}>
            <DialogTitle>{intl.formatMessage(title)}</DialogTitle>
            {subtitle ? <Body1>{intl.formatMessage(subtitle)}</Body1> : null}
          </div>
          <DialogContent>
            <Body2>
              {intl.formatMessage(copy, {
                br: <br />,
                termslink: function Link(str) {
                  // @ts-ignore formatjs/formatjs#3707
                  return (
                    <TextLink href={copilotTerms} target="_blank">
                      {str}
                    </TextLink>
                  ) as string;
                },
              })}
            </Body2>
          </DialogContent>
          <div className={classes.footer}>
            <Body1>
              {intl.formatMessage(
                {
                  defaultMessage: '{step, number} of {totalSteps, number}',
                  description: 'step counter',
                },
                {
                  step: step + 1,
                  totalSteps: steps.length,
                }
              )}
            </Body1>
            <DialogActions>
              {allowBack && (
                <Button
                  appearance="secondary"
                  onClick={() => {
                    setStep((step) => step - 1);
                  }}
                  className={classes.button}
                >
                  {intl.formatMessage(backLabel)}
                </Button>
              )}
              {step === steps.length - 1 ? (
                <DialogTrigger>
                  <Button appearance="primary" className={classes.button}>
                    {intl.formatMessage(closeLabel)}
                  </Button>
                </DialogTrigger>
              ) : (
                <Button appearance="primary" onClick={handleNext} className={classes.button}>
                  {intl.formatMessage(nextLabel)}
                </Button>
              )}
            </DialogActions>
          </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

const previewLabel = defineMessage({ defaultMessage: 'Preview', description: 'Status label for Copilot' });
const backLabel = defineMessage({ defaultMessage: 'Back', description: 'button label' });
const nextLabel = defineMessage({ defaultMessage: 'Next', description: 'button label' });
const closeLabel = defineMessage({ defaultMessage: 'Try Copilot', description: 'button label' });

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    rowGap: tokens.spacingVerticalXXXL,
    height: '566px',
  },
  header: {
    position: 'absolute',
    right: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  previewLabel: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('0px', '4px'),
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    backgroundColor: tokens.colorNeutralBackground4,
    height: '16px',
    width: 'fit-content',
  },
  image: {
    width: '256px',
    alignSelf: 'center',
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    ...shorthands.gap('4px'),
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'auto',
  },
  button: {
    minWidth: 0,
  },
});
